import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Printer, FileCsv } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LOGO_1 = "https://customer-assets.emergentagent.com/job_medform-standby/artifacts/3xn9wu25_standby_transparent.png";
const LOGO_2 = "https://customer-assets.emergentagent.com/job_medform-standby/artifacts/v4idzbss_on_site_transparent.png";

const TAG_BG = {
  Green: 'bg-green-500',
  Yellow: 'bg-yellow-400',
  Orange: 'bg-orange-500',
  Red: 'bg-red-600',
  Black: 'bg-slate-900',
  Untagged: 'bg-slate-300',
};

function formatDateShort(d) {
  if (!d) return '';
  return d;
}

function getQuickRange(key) {
  const now = new Date();
  const fmt = (d) => d.toISOString().split('T')[0];
  const today = fmt(now);
  switch (key) {
    case 'today': return { start: today, end: today };
    case 'week': {
      const s = new Date(now);
      s.setDate(s.getDate() - s.getDay());
      return { start: fmt(s), end: today };
    }
    case 'month': {
      const s = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: fmt(s), end: today };
    }
    case 'quarter': {
      const qm = Math.floor(now.getMonth() / 3) * 3;
      const s = new Date(now.getFullYear(), qm, 1);
      return { start: fmt(s), end: today };
    }
    case 'year': {
      const s = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      return { start: fmt(s), end: today };
    }
    case 'all': {
      return { start: '2020-01-01', end: today };
    }
    default: return { start: today, end: today };
  }
}

export function ReportsTab() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('all');
  const [events, setEvents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quickRange, setQuickRange] = useState('year');

  useEffect(() => {
    axios.get(`${API_URL}/api/events`).then(res => setEvents(res.data)).catch(() => {});
    // Auto-set to Year range
    const range = getQuickRange('year');
    setStartDate(range.start);
    setEndDate(range.end);
  }, []);

  // Auto-run report when dates change
  const fetchAnalytics = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const params = { start_date: startDate, end_date: endDate };
      if (selectedEvent && selectedEvent !== 'all') params.event_name = selectedEvent;
      const res = await axios.get(`${API_URL}/api/reports/analytics`, { params });
      setAnalytics(res.data);
    } catch (err) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedEvent]);

  useEffect(() => {
    if (startDate && endDate) fetchAnalytics();
  }, [fetchAnalytics, startDate, endDate]);

  const handleQuickRange = (key) => {
    setQuickRange(key);
    const range = getQuickRange(key);
    setStartDate(range.start);
    setEndDate(range.end);
  };

  const handleExportCSV = () => {
    if (!analytics || !analytics.roster) return;
    const rows = [['PCR #', 'Patient Name', 'Gender', 'Reason for Treatment', 'Transported', 'Event', 'Triage Center']];
    analytics.roster.forEach(r => {
      rows.push([r.form_number, `${r.first_name} ${r.last_name}`, r.gender, r.reason_for_treatment, r.transported, r.event_name, r.triage_center]);
    });
    const csv = rows.map(r => r.map(c => `"${(c || '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `event_triage_report_${startDate}_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const ov = analytics?.overview || {};
  const triage = analytics?.triage_distribution || {};
  const dailyVol = analytics?.daily_volume || [];
  const eventBreakdown = analytics?.event_breakdown || [];
  const centerBreakdown = analytics?.center_breakdown || [];
  const roster = analytics?.roster || [];
  const meta = analytics?.meta || {};
  const busiest = analytics?.busiest_day || {};

  const maxDaily = useMemo(() => Math.max(...dailyVol.map(d => d.count), 1), [dailyVol]);

  return (
    <div className="space-y-6">
      {/* Screen Header */}
      <div className="flex items-center justify-between no-print">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Admin Suite · Reports</h2>
          <p className="text-sm text-slate-500">Overall patient volume, tag distribution, and per-event / per-center breakdowns.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-sm" onClick={() => window.print()} data-testid="print-pdf-button">
            <Printer size={16} className="mr-2" /> Print / PDF
          </Button>
          <Button className="bg-slate-900 hover:bg-slate-800 rounded-sm" onClick={handleExportCSV} data-testid="export-csv-btn">
            <FileCsv size={16} className="mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 no-print">
        <CardContent className="py-4 space-y-4">
          <div className="flex items-center gap-4 flex-wrap">
            <fieldset className="border border-slate-200 rounded px-3 py-2">
              <legend className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 px-1">Quick Range</legend>
              <div className="flex gap-1">
                {['today', 'week', 'month', 'quarter', 'year', 'all'].map(k => (
                  <button
                    key={k}
                    onClick={() => handleQuickRange(k)}
                    className={`px-3 py-1 text-xs font-semibold rounded transition-colors ${quickRange === k ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                    data-testid={`range-${k}`}
                  >
                    {k.toUpperCase()}
                  </button>
                ))}
              </div>
            </fieldset>
            <div className="flex items-center gap-3">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">Start Date</label>
                <Input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setQuickRange(''); }} className="rounded-sm w-40" data-testid="start-date" />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">End Date</label>
                <Input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setQuickRange(''); }} className="rounded-sm w-40" data-testid="end-date" />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">Event</label>
              <select
                value={selectedEvent}
                onChange={e => setSelectedEvent(e.target.value)}
                className="h-10 rounded-sm border border-slate-300 bg-white px-3 text-sm min-w-[200px]"
                data-testid="event-filter-select"
              >
                <option value="all">All Events</option>
                {events.map(ev => <option key={ev.id} value={ev.name}>{ev.name}</option>)}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && <div className="text-center py-8 text-slate-500">Loading analytics...</div>}

      {analytics && !loading && (
        <>
          {/* Overview Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 print:grid-cols-5" data-testid="overview-cards">
            {[
              { label: 'Total Patients', value: ov.total_patients, sub: '' },
              { label: 'Transported', value: ov.transported, sub: ov.transported_pct ? `${ov.transported_pct}%` : '' },
              { label: 'Treat & Release', value: ov.treat_release, sub: '' },
              { label: 'Submitted', value: ov.submitted, sub: '' },
              { label: 'In Triage (Open)', value: ov.in_triage, sub: '' },
            ].map(c => (
              <Card key={c.label} className="border-slate-200">
                <CardContent className="py-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{c.label}</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{c.value ?? 0}</p>
                  {c.sub && <p className="text-xs text-slate-400 mt-0.5">{c.sub}</p>}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Triage Tag Distribution */}
          <Card className="border-slate-200">
            <CardContent className="py-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-3">Triage Tag Distribution</p>
              <div className="grid grid-cols-6 gap-3" data-testid="triage-distribution">
                {['Green', 'Yellow', 'Orange', 'Red', 'Black', 'Untagged'].map(tag => (
                  <div key={tag} className="text-center">
                    <div className={`${TAG_BG[tag]} rounded-lg py-3 px-2 mb-1`}>
                      <span className={`text-2xl font-bold ${tag === 'Untagged' ? 'text-slate-700' : 'text-white'}`}>
                        {triage[tag] ?? 0}
                      </span>
                    </div>
                    <span className={`text-xs font-medium ${tag === 'Untagged' ? 'text-slate-500' : ''}`}>{tag}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Daily Volume */}
          {dailyVol.length > 0 && (
            <Card className="border-slate-200 no-print">
              <CardContent className="py-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Daily Volume</p>
                  <p className="text-xs text-slate-400">{dailyVol.length} day{dailyVol.length !== 1 ? 's' : ''} with activity · Busiest: {busiest.date} ({busiest.count})</p>
                </div>
                <div className="flex items-end gap-2 h-44 overflow-x-auto pb-6" data-testid="daily-volume-chart">
                  {dailyVol.map(d => (
                    <div key={d.date} className="flex flex-col items-center min-w-[36px]">
                      <span className="text-xs font-bold text-slate-900 mb-1">{d.count}</span>
                      <div
                        className="bg-slate-800 rounded-t w-7 transition-all"
                        style={{ height: `${Math.max((d.count / maxDaily) * 120, 6)}px` }}
                      />
                      <span className="text-[10px] text-slate-500 mt-1.5 whitespace-nowrap">{d.date.slice(5)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Per-Event Breakdown */}
          {eventBreakdown.length > 0 && (
            <Card className="border-slate-200">
              <CardContent className="py-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-3">Per-Event Breakdown</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="event-breakdown-table">
                    <thead>
                      <tr className="bg-slate-800 text-white">
                        <th className="px-4 py-2 text-left font-medium">Event</th>
                        <th className="px-3 py-2 text-right font-medium">Total</th>
                        <th className="px-3 py-2 text-right font-medium">Trans.</th>
                        <th className="px-3 py-2 text-right font-medium">T&R</th>
                        <th className="px-3 py-2 text-right font-medium text-green-300">G</th>
                        <th className="px-3 py-2 text-right font-medium text-yellow-300">Y</th>
                        <th className="px-3 py-2 text-right font-medium text-orange-300">O</th>
                        <th className="px-3 py-2 text-right font-medium text-red-300">R</th>
                        <th className="px-3 py-2 text-right font-medium">B</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {eventBreakdown.map(eb => (
                        <tr key={eb.event} className="hover:bg-slate-50">
                          <td className="px-4 py-2">{eb.event}</td>
                          <td className="px-3 py-2 text-right font-semibold">{eb.total}</td>
                          <td className="px-3 py-2 text-right">{eb.transported}</td>
                          <td className="px-3 py-2 text-right">{eb.treat_release}</td>
                          <td className="px-3 py-2 text-right font-semibold text-green-700">{eb.Green}</td>
                          <td className="px-3 py-2 text-right font-semibold text-yellow-700">{eb.Yellow}</td>
                          <td className="px-3 py-2 text-right font-semibold text-orange-700">{eb.Orange}</td>
                          <td className="px-3 py-2 text-right font-semibold text-red-700">{eb.Red}</td>
                          <td className="px-3 py-2 text-right font-semibold">{eb.Black}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Per-Triage-Center Breakdown */}
          {centerBreakdown.length > 0 && (
            <Card className="border-slate-200">
              <CardContent className="py-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-3">Per-Triage-Center Breakdown</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="center-breakdown-table">
                    <thead>
                      <tr className="bg-slate-800 text-white">
                        <th className="px-4 py-2 text-left font-medium">Triage Center</th>
                        <th className="px-4 py-2 text-right font-medium">Total</th>
                        <th className="px-4 py-2 text-right font-medium">Transported</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {centerBreakdown.map(cb => (
                        <tr key={cb.center} className="hover:bg-slate-50">
                          <td className="px-4 py-2">{cb.center}</td>
                          <td className="px-4 py-2 text-right font-semibold">{cb.total}</td>
                          <td className="px-4 py-2 text-right">{cb.transported}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Patient Roster */}
          <Card className="border-slate-200">
            <CardContent className="py-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-3">Patient Roster ({roster.length})</p>
              {roster.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="patient-roster-table">
                    <thead>
                      <tr className="bg-slate-800 text-white">
                        <th className="px-4 py-2 text-left font-medium">PCR #</th>
                        <th className="px-4 py-2 text-left font-medium">Patient Name</th>
                        <th className="px-4 py-2 text-left font-medium">Gender</th>
                        <th className="px-4 py-2 text-left font-medium">Reason for Treatment</th>
                        <th className="px-4 py-2 text-left font-medium">Transported</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {roster.map(r => (
                        <tr key={r.id} className="hover:bg-slate-50">
                          <td className="px-4 py-2 font-mono text-xs">{r.form_number}</td>
                          <td className="px-4 py-2 font-medium">{r.first_name} {r.last_name}</td>
                          <td className="px-4 py-2">{r.gender}</td>
                          <td className="px-4 py-2">{r.reason_for_treatment}</td>
                          <td className="px-4 py-2">{r.transported}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-lg font-semibold text-slate-700">No Patients Reported</p>
                  <p className="text-sm text-slate-500 mt-1">{meta.event_filter} · {meta.start_date} — {meta.end_date}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* No data overall */}
          {ov.total_patients === 0 && (
            <div className="text-center py-4 text-slate-500 text-sm no-print">
              No patient records found for the selected criteria. The report is still printable.
            </div>
          )}
        </>
      )}

      {/* Print-Only Layout */}
      <div className="hidden print:block">
        <style>{`
          @media print {
            .no-print { display: none !important; }
            .print-container { break-inside: avoid; }
            body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          }
        `}</style>
        <div className="mb-6 border-b pb-4">
          <div className="flex items-center gap-4 mb-4">
            <img src={LOGO_1} alt="Logo 1" className="h-10" />
            <img src={LOGO_2} alt="Logo 2" className="h-10" />
          </div>
          <p className="text-sm text-slate-500 uppercase tracking-wider">On Site Medical Consultants LLC</p>
          <h1 className="text-2xl font-bold text-slate-900">OSMC Event Triage — Reports</h1>
          <p className="text-sm text-slate-500">
            Range: {meta.start_date} → {meta.end_date} · Event: {meta.event_filter} · Generated: {meta.generated_at}
          </p>
        </div>
      </div>
    </div>
  );
}
